import { z } from 'zod';
import { dateOnlySchema } from './patients.js';

/**
 * Contrat réseau des remplacements (décision de Nicolas du 30 septembre 2026).
 *
 * Un remplaçant n'exerce **que** pendant un remplacement : il remplace explicitement un praticien
 * permanent — propriétaire, collaborateur ou salarié — **du** premier jour **au** dernier, bornes
 * incluses, toutes deux obligatoires (« dates obligatoires »). Pendant la période, le remplacé n'a
 * ni séance ni facture ; hors période, le remplaçant non plus. Deux remplacements ne se
 * chevauchent ni pour un même remplacé, ni pour un même remplaçant.
 *
 * Les dates sont des jours civils du cabinet (`YYYY-MM-DD`), comme les périodes de conditions
 * d'un membre : un remplacement se pense en journées, pas en instants.
 */

const periodSchema = z
  .object({
    replacedMemberId: z.uuid(),
    startsOn: dateOnlySchema,
    endsOn: dateOnlySchema,
    /**
     * Rendez-vous déjà posés au nom du remplacé dans la période : sans ce drapeau, le serveur
     * refuse (`409 replacement_appointments_to_reassign`) et dit combien il y en a ; avec lui, il
     * les réattribue au remplaçant **dans la même transaction**. Rien ne bouge sans qu'on l'ait
     * demandé, et rien n'est à moitié fait.
     */
    reassignAppointments: z.boolean().optional(),
  })
  .strict();

const endsAfterStart = (value: { startsOn: string; endsOn: string }) =>
  value.endsOn >= value.startsOn;
const ENDS_BEFORE_START = 'La fin du remplacement précède son début';

export const replacementCreateSchema = periodSchema
  .extend({ substituteMemberId: z.uuid() })
  .strict()
  .refine(endsAfterStart, ENDS_BEFORE_START);
export type ReplacementCreateInput = z.infer<typeof replacementCreateSchema>;

/** `PUT` : la période est remplacée en entier. Le remplaçant, lui, ne change pas. */
export const replacementUpdateSchema = periodSchema.refine(endsAfterStart, ENDS_BEFORE_START);
export type ReplacementUpdateInput = z.infer<typeof replacementUpdateSchema>;

export const replacementIdParamSchema = z.object({ replacementId: z.uuid() }).strict();

export const replacementListQuerySchema = z.object({ memberId: z.uuid().optional() }).strict();
export type ReplacementListQuery = z.infer<typeof replacementListQuerySchema>;

export interface ReplacementDto {
  id: string;
  substituteMemberId: string;
  replacedMemberId: string;
  startsOn: string;
  endsOn: string;
  createdAt: string;
}

export interface ReplacementListDto {
  replacements: ReplacementDto[];
}

/**
 * Détail du refus `replacement_appointments_to_reassign` : combien de rendez-vous du remplacé
 * tombent dans la période, et lesquels — plages seules, **sans aucune donnée patient**, comme le
 * 409 d'un chevauchement.
 */
export interface ReplacementAppointmentsDetailsDto {
  count: number;
  appointments: { id: string; startsAt: string; endsAt: string }[];
}
